import { useEffect, useState } from "react";
import { ApiError, fetchUsers, type UserRowDto } from "../api";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

function DisabledAction({ label, endpoint }: { label: string; endpoint: string }) {
  return (
    <button className="btn ghost sm" disabled title={`Needs ${endpoint} -- Phase 10 write-path (invite/reset), not built this session`}>
      {label}
    </button>
  );
}

function initials(name: string | null, email: string): string {
  const src = name ?? email;
  const parts = src.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function UsersPage({ token, onUnauthorized, currentUserId }: { token: string; onUnauthorized: () => void; currentUserId: string }) {
  const [users, setUsers] = useState<UserRowDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers(token)
      .then(setUsers)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        setError(err instanceof ApiError ? err.message : "request failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { sorted: sortedUsers, sortKey, sortDir, toggleSort } = useSort<UserRowDto>(
    users ?? [],
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  function exportCsv() {
    exportRowsAsCsv(
      (users ?? []) as unknown as Record<string, unknown>[],
      [
        { key: "email", label: "Email" },
        { key: "display_name", label: "Name" },
        { key: "role", label: "Role" },
        { key: "enabled", label: "Status" },
        { key: "last_login_at", label: "Last active" },
        { key: "created_at", label: "Added" },
      ],
      "users.csv",
    );
  }

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <div className="page-sub">Invite-only. Read-only this build — invite/disable/reset need the Phase 10 write-path.</div>
        </div>
        <DisabledAction label="+ Invite user" endpoint="POST /users/invite" />
      </div>

      <PageHelper>
        The full invite-only user roster: role, active/disabled status, and last login. Read-only — invite, reset,
        and disable actions need Phase 10's write-path.
      </PageHelper>

      <div className="banner show" style={{ display: "flex" }}>
        <span>ⓘ</span>
        <span>
          The <code>users</code> table is genuinely near-empty — this is the closed-invite state, not a loading
          placeholder: {users?.length ?? "…"} account(s) total. Invite / resend / revoke / disable / re-enable are
          all disabled: invite specifically needs Phase 10&apos;s email-link provisioning flow, which this build does
          not implement.
        </span>
      </div>

      <div className="card">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button className="btn ghost sm" onClick={exportCsv}>↓ CSV</button>
        </div>
        <table className="stocks-table">
          <thead>
            <tr>
              <th className="l clickable" onClick={() => toggleSort("email")}>User {sortGlyph("email", sortKey, sortDir)}</th>
              <th className="clickable" onClick={() => toggleSort("role")}>Role {sortGlyph("role", sortKey, sortDir)}</th>
              <th className="clickable" onClick={() => toggleSort("enabled")}>Status {sortGlyph("enabled", sortKey, sortDir)}</th>
              <th className="clickable" onClick={() => toggleSort("last_login_at")}>Last active {sortGlyph("last_login_at", sortKey, sortDir)}</th>
              <th className="clickable" onClick={() => toggleSort("created_at")}>Added {sortGlyph("created_at", sortKey, sortDir)}</th>
              <th className="r">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((u) => (
              <tr key={u.user_id}>
                <td className="l">
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%",
                      background: u.role === "admin" ? "var(--ink)" : "var(--accent)",
                      color: "#fff", display: "grid", placeItems: "center",
                      fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.76rem", flexShrink: 0,
                    }}>
                      {initials(u.display_name, u.email)}
                    </div>
                    <div>
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{u.display_name ?? u.email}</div>
                      <div className="note" style={{ fontSize: "0.76rem" }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td><span className={`chip ${u.role === "admin" ? "st-challenger" : ""}`}>{u.role}</span></td>
                <td><span className={`chip ${u.enabled ? "st-champion" : ""}`}>{u.enabled ? "active" : "disabled"}</span></td>
                <td className="mono" style={{ fontSize: "0.78rem", color: "var(--ink-faint)" }}>
                  {u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : "never"}
                </td>
                <td className="note" style={{ fontSize: "0.78rem" }}>{u.created_at ? u.created_at.slice(0, 10) : "—"}</td>
                <td className="r">
                  {u.user_id === currentUserId ? (
                    <span className="mut">you</span>
                  ) : (
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <DisabledAction label="Send reset" endpoint="POST /users/{id}/reset" />
                      <DisabledAction label={u.enabled ? "Disable" : "Re-enable"} endpoint="PATCH /users/{id}" />
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {users && users.length === 0 && (
              <tr><td colSpan={6} className="mut" style={{ textAlign: "center", padding: 20 }}>No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
